import { FireCell, FireState, Plane } from "@firesim/simkit-core";
import { TerrainCodes } from "./terrain-types";
import { simkitWindSpeedToNormalized, simkitWindTodegrees, SmokeWorkerManager, windDirectionToVectorCached } from "@firesim/smoke-simulation";

export function setupSmoke(plane: Plane<TerrainCodes, FireCell<TerrainCodes>>, state: FireState<TerrainCodes, FireCell<TerrainCodes>> ) {

        // Setup smoke simulation using the new headless SmokeWorkerManager
        const smokeManager = new SmokeWorkerManager({
            width: plane.width,
            height: plane.height,
            smokeConfig: {
                fadeRate: 0.02,
                windInfluence: 1.5,
                smokeOpacity: 0.02,
                smokeRadius: 3,
                performanceMode: 'high',
                enableBlur: true,
                blurRadius: 1.5
            }
        });

        // Assign a callback to handle the rendered image from the worker
        smokeManager.onRender = (bitmap) => {
            plane.clearRect(0, 0, plane.width, plane.height, 'smoke');
            plane.loadImage(bitmap, { layer: 'smoke', scaleToCanvas: true, scaleCanvasToImage: false });
        };

        let lastSimulationTick = plane.tick || 0;
        let isRunning = false;
        let cachedFireSources: { x: number; y: number; intensity: number }[] = [];

        // Create the render loop to drive the smoke simulation
        function smokeRenderLoop() {
            if (!isRunning) return;

            const currentTick = plane.tick || 0;

            // Refresh fire sources and wind when the simulation tick advances
            if (currentTick !== lastSimulationTick) {
                cachedFireSources = [];
                // Iterate all agent types — we can't rely on constructor.name
                // because esbuild may mangle class names (e.g. FrontAgent -> _FrontAgent).
                for (const agents of Object.values(plane.activeAgents)) {
                    agents.forEach(agent => {
                        if (agent.cell) {
                            cachedFireSources.push({ x: agent.cell.x, y: agent.cell.y, intensity: 0.8 });
                        }
                    });
                }

                const windDirection = state.windDirection;
                const windSpeed = state.currentWindSpeed || state.windSpeed;
                const normalizedSpeed = simkitWindSpeedToNormalized(windSpeed);
                const [x,y] = state.windDirectionVector;
                const windVector = {
                    x: x * normalizedSpeed,
                    y: y * normalizedSpeed,
                    speed: normalizedSpeed
                };
                smokeManager.updateWind(windVector);

                lastSimulationTick = currentTick;
            }

            // Render every frame so smoke drifts and fades smoothly between ticks
            smokeManager.render(cachedFireSources);

            requestAnimationFrame(smokeRenderLoop);
        }

        // Set up event listeners on the plane to control the smoke simulation
        plane.addEventListener('start', () => {
            if (!isRunning) {
                isRunning = true;
                requestAnimationFrame(smokeRenderLoop);
            }
        });

        plane.addEventListener('stop', () => {
            isRunning = false;
        });

        plane.addEventListener('reset', () => {
            smokeManager.clear();
            cachedFireSources = [];
            lastSimulationTick = plane.tick || 0;
        });


}
